import React from 'react';

/**
 * Surface card.
 *
 * Metrics from the design reference
 * (docs/revamp-july-2026/handoff/Focal Point Site.dc.html): --card background
 * on a --line hairline, 12px radius, 32px top / 30px side / 34px bottom
 * padding. The reversed variant drops the border and sits on --ink-blue, which
 * the reference uses to mark the core offer among a row of cards.
 *
 * The reversed variant rebinds --text-3 and --line on its own element rather
 * than taking an `onBlue` prop, so anything nested inside it (an Eyebrow, a
 * divider) recolours through the cascade. These are unwrapped tokens, not
 * var(--color-*), so they re-resolve correctly in a subtree.
 *
 * Replaces the previous glass-morphism Card and IconCard, which had no
 * consumers and no counterpart in the new design.
 */

interface CardProps {
  /**
   * 'default' is --card on a --line hairline.
   * 'reversed' is --ink-blue with no border and on-blue text.
   */
  variant?: 'default' | 'reversed';
  className?: string;
  children?: React.ReactNode;
}

export default function Card({
  variant = 'default',
  className = '',
  children,
}: CardProps) {
  const base = 'rounded-xl px-[30px] pt-8 pb-[34px]';

  const variantClasses =
    variant === 'reversed'
      ? 'bg-ink-blue text-on-blue [--text-3:var(--on-blue-3)] [--line:var(--line-2)]'
      : 'bg-card border border-line text-body';

  return <div className={`${base} ${variantClasses} ${className}`.trim()}>{children}</div>;
}
